import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-relationship-list>` says about itself.
 *
 * A list of relationship names is almost useless, and it is what most tools show. The three things a
 * person needs here are which OBJECT points at this one, through which FIELD, and what happens to
 * those records if this one is deleted. The third is why anybody opens the screen, and it is the one
 * most often missing.
 *
 * So the delete behaviour is spelled out in words beside the vocabulary word. "cascade" is a word a
 * developer knows and a word that does not say what it does; the difference between cascade and
 * restrict is the difference between losing four thousand records and being told you cannot.
 *
 * And a behaviour the caller did not state reads "not stated" rather than defaulting to the
 * safe-sounding one. Printing "restrict" over a relationship nobody checked is how somebody deletes
 * a record believing they were protected.
 */
const docs: ComponentDocs = {
  aliases: ['child relationships', 'related objects', 'reverse references', 'what points here', 'foreign keys'],

  answers: [
    'find out what points at this object',
    'see what a delete would take with it',
    'find which field holds a reference to this object',
    'see how many child records a relationship has',
  ],

  placements: ['appPage', 'recordPage'],

  whenToUse:
    'Before changing or deleting something, when the question is what ELSE depends on it. It is the ' +
    'reverse view of a lookup: not what this points at, but what points here.',

  insteadUse: [
    {
      name: 'ObjectFieldExplorer',
      when: 'the question is what is ON this object rather than what points at it.',
    },
    {
      name: 'DataTable',
      when: 'the rows are related RECORDS being listed, not the relationships themselves.',
    },
  ],

  inputs: [
    {
      name: 'relationships',
      type: 'json',
      description:
        'What points here: `{ name, childObject, field, onDelete?, count? }[]`. `onDelete` absent ⇒ ' +
        'NOT STATED, which is drawn as "not stated" and never guessed. `count` absent ⇒ not counted, ' +
        'which is not the same as none.',
      default: [],
    },
    {
      name: 'linkable',
      attribute: 'linkable',
      type: 'boolean',
      description:
        'Make the object names pressable, raising `caos-relationship-open`. Off by default, because a ' +
        'link to somewhere the host has not wired is worse than plain text.',
      default: false,
    },
    {
      name: 'emptyText',
      attribute: 'empty-text',
      type: 'text',
      description:
        'What to say when nothing points here. "Nothing points at this object" is a real and useful ' +
        'answer, so it is stated rather than left as a blank table.',
      default: 'Nothing points at this object.',
    },
    {
      name: 'sortKey',
      attribute: 'sort-key',
      type: 'text',
      options: ['childObject', 'name', 'field', 'onDelete', 'count'],
      description: 'Which column orders the rows.',
      default: 'childObject',
    },
    {
      name: 'sortDirection',
      attribute: 'sort-direction',
      type: 'text',
      options: ['asc', 'desc'],
      description: 'Which way. An uncounted relationship sorts last in both directions.',
      default: 'asc',
    },
  ],

  events: [
    {
      name: 'caos-relationship-open',
      when: 'An object name is pressed, with `linkable` set.',
      carries: '`{ object }`.',
    },
  ],

  state: [
    {
      name: 'cascading',
      type: 'CaosRelationship[]',
      description:
        'The relationships that would take records with this one. Read it to build a delete warning ' +
        'without re-deciding which behaviours are dangerous.',
    },
    { name: 'ordered', type: 'CaosRelationship[]', description: 'The relationships in the order they are shown.' },
  ],

  parts: [
    { part: 'list', is: 'The outer column.' },
    { part: 'summary', is: 'The count line, including how many would cascade.' },
    { part: 'table', is: 'The composed data table.' },
    { part: 'object-link', is: 'A pressable object name.', when: '`linkable` is set.' },
    { part: 'behaviour', is: 'The on-delete cell.' },
    { part: 'count', is: 'The record-count cell.' },
  ],

  accessibility:
    'The summary is a `role="status"` live region and names the cascading count in words as well as ' +
    'in red, so "four of these would delete records with this one" is heard rather than only seen. ' +
    'Each delete behaviour is a sentence, not a vocabulary word, so a reader who does not already ' +
    'know what "cascade" means is not left to guess at the most consequential cell on the screen. ' +
    'Object names are `<button>` elements with an accessible name reading "Open <object>" when they ' +
    'are pressable, and ordinary text when they are not — never a link-coloured span that does ' +
    'nothing.',

  examples: [
    {
      key: 'cascade',
      label: 'What a delete would take with it',
      summary:
        'Two relationships cascade, one restricts, one empties the reference — each said in words ' +
        'beside its name, and the summary counts the dangerous ones.',
      placement: {
        component: 'caos-relationship-list',
        inputs: {
          linkable: true,
          relationships: [
            { name: 'Contacts', childObject: 'Contact', field: 'AccountId', onDelete: 'cascade', count: 4120 },
            { name: 'Opportunities', childObject: 'Opportunity', field: 'AccountId', onDelete: 'cascade', count: 860 },
            { name: 'Invoices', childObject: 'Invoice__c', field: 'Account__c', onDelete: 'restrict', count: 22 },
            { name: 'Notes', childObject: 'Note', field: 'ParentId', onDelete: 'clear', count: 0 },
          ],
        },
      },
      stage: { needsWidth: 800 },
    },
    {
      key: 'unstated',
      label: 'What nobody checked',
      summary:
        'A behaviour the caller did not state reads "not stated", and a relationship nobody counted ' +
        'reads "not counted" — neither is quietly rendered as the reassuring answer.',
      placement: {
        component: 'caos-relationship-list',
        inputs: {
          relationships: [
            { name: 'Contacts', childObject: 'Contact', field: 'AccountId', onDelete: 'cascade', count: 4120 },
            { name: 'Cases', childObject: 'Case', field: 'AccountId' },
            { name: 'Assets', childObject: 'Asset', field: 'AccountId', onDelete: 'restrict' },
          ],
        },
      },
      stage: { needsWidth: 800 },
    },
    {
      key: 'none',
      label: 'Nothing points here',
      summary: 'A real answer, stated — not a blank table somebody reads as a screen that failed to load.',
      placement: { component: 'caos-relationship-list', inputs: { relationships: [] } },
      stage: { needsWidth: 800 },
    },
  ],
};

export default docs;
